import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  Grid,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  Modal,
  InputLabel,
  CircularProgress,
  Select,
  Collapse,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CustomTextField from "../../../Utils/CustomTextfield";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAdmin,
  getAllAdmins,
  registeration,
  updateAdmin,
} from "../../../api/action";
import CustomSnackbar from "../../../Utils/CustomSnackbar";
import CustomPagination from "../../../Utils/CustomPagination";
import DeleteModal from "../../../Utils/DeleteModal";
import { phoneRegex } from "../../../Utils/Regex";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { adminAddSchema, adminEditSchema } from "../../../Utils/validationSchemas";
import FormTextField from "../../../Utils/FormTextField";
import { sanitizeInput, collapseSpaces, normalizePhone } from "../../../Utils/validationSecurity";

const ROWS_PER_PAGE = 10;

const AdminsBody = ({ onEdit, onDelete }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchAdminName, setSearchAdminName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchPhoneNumber, setSearchPhoneNumber] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [loading, setLoading] = useState("");
  const [updated, setUpdated] = useState(false);
  const [adminId, setAdminID] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [newAdminUserRole, setNewAdminUserRole] = useState("Admin");
  const userRole = useSelector((state) => state?.user?.userRole);
  const [editStatus, setEditStatus] = useState(false);
  const [userId, setUserId] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const addForm = useForm({
    resolver: yupResolver(adminAddSchema),
    mode: "onChange",
    defaultValues: {
      full_name: "",
      email: "",
      phone_number: "",
      password: "",
    },
  });

  const editForm = useForm({
    resolver: yupResolver(adminEditSchema),
    mode: "onChange",
    defaultValues: {
      full_name: "",
      phone_number: "",
      password: "",
    },
  });

  const generatePassword = () => {
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const special = "@$!%*?&";
    const allChars = uppercase + lowercase + numbers + special;

    let password = "";

    while (!passwordRegex.test(password)) {
      password = "";
      password += uppercase[Math.floor(Math.random() * uppercase.length)];
      password += lowercase[Math.floor(Math.random() * lowercase.length)];
      password += numbers[Math.floor(Math.random() * numbers.length)];
      password += special[Math.floor(Math.random() * special.length)];

      for (let i = 4; i < 12; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }

      password = password
        .split("")
        .sort(() => Math.random() - 0.5)
        .join("");
    }

    return password;
  };

  useEffect(() => {
    setLoading(true);
    const payload = null;
    dispatch(
      getAllAdmins(
        payload,
        (data) => {
          setLoading(false);
          setData(data?.data?.Data);
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      )
    );
  }, [updated]);

  const handleOpenEditModal = (selectedRow) => {
    setEditModalOpen(true);
    setAdminID(selectedRow.id);
    setEditStatus(selectedRow.is_active);
    setUserId(selectedRow.user_id);
    editForm.reset({
      full_name: selectedRow.Full_Name || "",
      phone_number: selectedRow.Phone_number || "",
      password: "",
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const selectedCodes = data.map((row) => row.facilityCode);
      setSelectedRows(selectedCodes);
    } else {
      setSelectedRows([]);
    }
    setSelectAll(event.target.checked);
  };

  const handleCheckboxChange = (facilityCode) => {
    const isSelected = selectedRows.includes(facilityCode);
    let updatedSelectedRows = [];
    if (isSelected) {
      updatedSelectedRows = selectedRows.filter(
        (code) => code !== facilityCode
      );
    } else {
      updatedSelectedRows = [...selectedRows, facilityCode];
    }
    setSelectedRows(updatedSelectedRows);
    setSelectAll(updatedSelectedRows.length === data.length);
  };

  const handleDelete = () => {
    onDelete(selectedRows);
    setSelectedRows([]);
  };

  const handleAddNew = () => {
    setShowModal(true);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchAdminName, searchAdminName, searchPhoneNumber]);

  const filteredData = data.filter((row) => {
    const matchesAdminName = row.Full_Name.toLowerCase().includes(
      searchAdminName.toLowerCase()
    );
    const matchesEmail = row.email
      .toLowerCase()
      .includes(searchEmail.toLowerCase());
    const matchesPhoneNumber = row.phoneNumber
      ? row.phoneNumber.includes(searchPhoneNumber)
      : true;
    return matchesAdminName && matchesEmail && matchesPhoneNumber;
  });

  const handleClearSearch = () => {
    setSearchAdminName("");
    setSearchEmail("");
    setSearchPhoneNumber("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    addForm.reset();
    setPasswordMessage("");
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setPasswordMessage("");
  };

  const onAddSubmit = (formData) => {
    const payload = {
      email: sanitizeInput(formData.email),
      password: formData.password,
      password2: formData.password,
      full_name: sanitizeInput(formData.full_name),
      phone_number: normalizePhone(sanitizeInput(formData.phone_number)),
      user_role: newAdminUserRole,
      is_active: true,
    };

    setLoading(true);
    dispatch(
      registeration(
        payload,
        (data) => {
          setLoading(false);
          setSnackbarMessage("Admin added successfully");
          setSnackbarOpen(true);
          setSnackbarSeverity("success");
          setShowModal(false);
          setUpdated((prevState) => !prevState);
          addForm.reset();
        },
        (error) => {
          console.log(error);
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.error ||
                "Oops! Looks like we need you to log in again to keep things secure!";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  const onEditSubmit = (formData) => {
    const payload = {
      full_name: sanitizeInput(formData.full_name),
      admin_id: adminId,
      Phone_number: normalizePhone(sanitizeInput(formData.phone_number)),
      phone_number: normalizePhone(sanitizeInput(formData.phone_number)),
      is_active: editStatus,
      user_id: userId,
    };
    if (formData.password) {
      payload.password = formData.password;
    }

    setLoading(true);
    dispatch(
      updateAdmin(
        payload,
        (data) => {
          setLoading(false);
          setSnackbarMessage("Admin updated successfully");
          setSnackbarOpen(true);
          setSnackbarSeverity("success");
          setEditModalOpen(false);
          setUpdated((prevState) => !prevState);
        },
        (error) => {
          console.log(error);
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.detail ||
                "Oops! Looks like we need you to log in again to keep things secure!";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  const handleDeleteAdmin = (user_id) => {
    const payload = {
      user_id,
    };

    setLoading(true);
    dispatch(
      deleteAdmin(
        payload,
        (data) => {
          setLoading(false);
          setSnackbarMessage("Admin deleted successfully");
          setSnackbarOpen(true);
          setSnackbarSeverity("success");
          setEditModalOpen(false);
          setUpdated((prevState) => !prevState);
          setOpenDeleteModal(false);
        },
        (error) => {
          console.log(error);
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.detail ||
                "Oops! Looks like we need you to log in again to keep things secure!";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDeleteModal = (id) => {
    setOpenDeleteModal(true);
    setAdminID(id);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + ROWS_PER_PAGE
  );
  const endIndex = startIndex + ROWS_PER_PAGE;

  const handleEditGeneratePassword = () => {
    const newPassword = generatePassword();
    editForm.setValue('password', newPassword, { shouldValidate: true });
    setPasswordMessage("✅ New password generated");
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    addForm.setValue('password', newPassword, { shouldValidate: true });
    setPasswordMessage("✅ New password generated");
  };

  return (
    <Box sx={{ px: 10, py: 2 }}>
      <DeleteModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        loading={loading}
        setLoading={setLoading}
        handleDeletefunction={handleDeleteAdmin}
        id={adminId}
      />
      <Typography
        variant="h6"
        sx={{ color: "rgba(29, 57, 105, 1)", fontWeight: "bold", mb: 2 }}
      >
        Search Admin
      </Typography>
      <Grid container spacing={5}>
        <Grid item md={4} xs={6}>
          <InputLabel>Admin Name</InputLabel>
          <CustomTextField
            value={searchAdminName}
            setValue={setSearchAdminName}
          />
        </Grid>

        <Grid item md={4} sm={6} xs={6}>
          <Box sx={{ display: "flex", justifyContent: "right" }}>
            <Button
              className="buttonPadding"
              variant="contained"
              onClick={handleClearSearch}
              sx={{
                boxShadow: "none !important",
                my: 3,
                mx: 1,
              }}
            >
              Clear
            </Button>
            <Button
              disabled={userRole !== "Admin"}
              className="buttonPadding"
              variant="contained"
              onClick={handleAddNew}
              sx={{
                background: userRole !== "Admin" ? "#d3d3d3" : null,
                boxShadow: "none !important",
                my: 3,
                mx: 1,
              }}
            >
              Add New
            </Button>
          </Box>
        </Grid>
      </Grid>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "40vh",
          }}
        >
          <CircularProgress sx={{ color: "rgba(29, 57, 105, 1) !important" }} />
        </Box>
      ) : filteredData.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "#555", mt: 15 }}
        >
          No Data Available
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} className="scrollBar">
            <Table>
              <TableHead
                sx={{
                  background: "rgba(29, 57, 105, 1)",
                  color: "#fff",
                }}
              >
                <TableRow>
                  <TableCell sx={{ color: "#fff", py: 0.5 }}>
                    Admin Name
                  </TableCell>
                  <TableCell sx={{ color: "#fff", py: 0.5 }}>Email ID</TableCell>
                  <TableCell sx={{ color: "#fff", py: 0.5 }}>
                    Phone Number
                  </TableCell>
                  <TableCell sx={{ color: "#fff", py: 0.5 }}>Role</TableCell>
                  <TableCell
                    sx={{ color: "#fff", py: 0, whiteSpace: "nowrap" }}
                  >
                    Status
                  </TableCell>
                  <TableCell sx={{ color: "#fff", py: 0.5 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.slice(startIndex, endIndex).map((row) => (
                  <TableRow key={row.facilityCode}>
                    <TableCell className="fs12" sx={{ py: 0 }}>
                      {row.Full_Name}
                    </TableCell>
                    <TableCell className="fs12" sx={{ py: 0 }}>
                      {row.email}
                    </TableCell>
                    <TableCell className="fs12" sx={{ py: 0 }}>
                      {row.Phone_number}
                    </TableCell>
                    <TableCell className="fs12" sx={{ py: 0 }}>
                      {row.user_role === "Admin"
                        ? "Super Admin"
                        : row.user_role === "Admin Operations"
                        ? "Operation Manager"
                        : row.user_role === "Admin Finance"
                        ? "Finance Manager"
                        : ""}
                    </TableCell>
                    <TableCell
                      className="fs12"
                      sx={{ py: 0.3, whiteSpace: "nowrap" }}
                    >
                      {row?.is_active === true ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell sx={{ py: 0.3 }}>
                      <IconButton
                        disabled={userRole !== "Admin"}
                        color="primary"
                        onClick={() => handleOpenEditModal(row)}
                      >
                        <EditIcon
                          sx={{
                            color: userRole !== "Admin" ? "#d3d3d3" : "rgba(29, 57, 105, 1)",
                            fontSize: "16px",
                          }}
                        />
                      </IconButton>
                      <IconButton
                        disabled={userRole !== "Admin"}
                        color="secondary"
                        onClick={() => handleDeleteModal(row.user_id)}
                      >
                        <DeleteIcon
                          sx={{
                            color: userRole !== "Admin" ? "#d3d3d3" : "rgba(29, 57, 105, 1)",
                            fontSize: "16px",
                          }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            count={Math.ceil(filteredData.length / ROWS_PER_PAGE)}
            page={currentPage}
            onChange={handleChangePage}
          />
        </>
      )}

      {/* ADD MODAL  */}

      <Modal
        open={showModal}
        onClose={handleCloseModal}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box
          sx={{
            background: "#fff",
            width: "40vw",
            px: 5,
            py: 5,
            mx: "auto",
            borderRadius: "6px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              verticalAlign: "middle",
            }}
          >
            <Typography
              className="fs16"
              sx={{ fontWeight: 600, color: "rgba(29, 57, 105, 1)" }}
            >
              Add New Admin
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon
                sx={{
                  color: "#fff",
                  background: "rgba(29, 57, 105, 1)",
                  borderRadius: "25px",
                  height: 20,
                  width: 20,
                  p: 0.5,
                }}
              />
            </IconButton>
          </Box>
          <Box sx={{ my: 2 }} component="form" onSubmit={addForm.handleSubmit(onAddSubmit)}>
            <Grid container spacing={3}>
              <Grid item md={6}>
                <InputLabel>Admin Name</InputLabel>
                <FormTextField
                  name="full_name"
                  register={addForm.register}
                  errors={addForm.formState.errors}
                  watch={addForm.watch}
                  maxLength={100}
                />
              </Grid>
              <Grid item md={6}>
                <InputLabel>Email</InputLabel>
                <FormTextField
                  name="email"
                  register={addForm.register}
                  errors={addForm.formState.errors}
                  watch={addForm.watch}
                  maxLength={254}
                />
              </Grid>
              <Grid item md={6}>
                <InputLabel>Phone Number</InputLabel>
                <FormTextField
                  name="phone_number"
                  register={addForm.register}
                  errors={addForm.formState.errors}
                  watch={addForm.watch}
                  maxLength={20}
                />
              </Grid>

              <Grid item md={6}>
                <InputLabel>User Role</InputLabel>
                <Select
                  value={newAdminUserRole}
                  onChange={(e) => setNewAdminUserRole(e.target.value)}
                  sx={{ width: "100%", height: "32px" }}
                >
                  <MenuItem value={"Admin"}>Admin</MenuItem>
                  <MenuItem value={"Admin Finance"}>Admin Finance</MenuItem>
                  <MenuItem value={"Admin Operations"}>
                    Admin Operations
                  </MenuItem>
                </Select>
              </Grid>

              <Grid item md={12}>
                <InputLabel>Password</InputLabel>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <FormTextField
                      name="password"
                      register={addForm.register}
                      errors={addForm.formState.errors}
                      watch={addForm.watch}
                      maxLength={128}
                      type="password"
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      onClick={handleGeneratePassword}
                      type="button"
                    >
                      <Typography variant="caption">
                        Generate Password
                      </Typography>
                    </Button>
                  </Grid>
                </Grid>

                <Collapse in={Boolean(passwordMessage)}>
                  <Typography variant="caption" sx={{ color: "green" }}>
                    {passwordMessage}
                  </Typography>
                </Collapse>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Password must be at least 8 characters long, and include one
                  uppercase letter, one lowercase letter, one number, and one
                  special character (@$!%*?&).
                </Typography>
              </Grid>
            </Grid>
            <Button
              variant="contained"
              type="submit"
              sx={{
                background: "rgba(29, 57, 105, 1) !important",
                boxShadow: "none !important",
                display: "block",
                mt: 3,
                ml: "auto",
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Add"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Modal  */}

      <Modal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box
          sx={{
            background: "#fff",
            width: "40vw",
            px: 5,
            py: 5,
            mx: "auto",
            borderRadius: "6px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              verticalAlign: "middle",
            }}
          >
            <Typography
              className="fs16"
              sx={{ fontWeight: 600, color: "rgba(29, 57, 105, 1)" }}
            >
              Edit Admin
            </Typography>
            <IconButton onClick={handleCloseEditModal}>
              <CloseIcon
                sx={{
                  color: "#fff",
                  background: "rgba(29, 57, 105, 1)",
                  borderRadius: "25px",
                  height: 20,
                  width: 20,
                  p: 0.5,
                }}
              />
            </IconButton>
          </Box>
          <Box sx={{ my: 2 }} component="form" onSubmit={editForm.handleSubmit(onEditSubmit)}>
            <Grid container spacing={3}>
              <Grid item md={6}>
                <InputLabel>Admin Name</InputLabel>
                <FormTextField
                  name="full_name"
                  register={editForm.register}
                  errors={editForm.formState.errors}
                  watch={editForm.watch}
                  maxLength={100}
                />
              </Grid>
              <Grid item md={6}>
                <InputLabel>Phone Number</InputLabel>
                <FormTextField
                  name="phone_number"
                  register={editForm.register}
                  errors={editForm.formState.errors}
                  watch={editForm.watch}
                  maxLength={20}
                />
              </Grid>

              <Grid item md={6} xs={6}>
                <InputLabel>Status</InputLabel>
                <Select
                  fullWidth
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  sx={{ height: "32px", padding: "5px 5px", fontSize: "12px" }}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </Grid>

              <Grid item md={12}>
                <InputLabel>Password</InputLabel>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <FormTextField
                      name="password"
                      register={editForm.register}
                      errors={editForm.formState.errors}
                      watch={editForm.watch}
                      maxLength={128}
                      type="password"
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      onClick={handleEditGeneratePassword}
                      type="button"
                    >
                      <Typography variant="caption">
                        Generate Password
                      </Typography>
                    </Button>
                  </Grid>
                </Grid>

                <Collapse in={Boolean(passwordMessage)}>
                  <Typography variant="caption" sx={{ color: "green" }}>
                    {passwordMessage}
                  </Typography>
                </Collapse>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Password must be at least 8 characters long, and include one
                  uppercase letter, one lowercase letter, one number, and one
                  special character (@$!%*?&).
                </Typography>
              </Grid>
            </Grid>
            <Button
              variant="contained"
              type="submit"
              sx={{
                boxShadow: "none !important",
                display: "block",
                mt: 3,
                ml: "auto",
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Update"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      <CustomSnackbar
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default AdminsBody;
